import { FilterList as FilterListIcon, PlusOne, SearchOff } from "@mui/icons-material";
import CachedIcon from "@mui/icons-material/Cached";
import {
  Badge,
  Box,
  Collapse,
  Grid2,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useRequisitionSearch } from "../../../hooks/Warehouse";
import { RequisitionFormType } from "../../../types/forms/warehouse/requisition";
import { EquipmentResponse } from "../../../types/model/warehouse/equipment";
import { RequisitionResponse } from "../../../types/model/warehouse/requisition";
import NewRequisitionPanel from "./NewRequisitionPanel";
import RequisitionRow from "./RequisitionRow";

interface WarehouseRequisitionsTableProps {
  requisitionsData: RequisitionResponse[];
  equipmentOptions: EquipmentResponse[];
  isLoadingEquipment?: boolean;
  canEdit: boolean;
  onCreate: (values: RequisitionFormType) => Promise<void>;
  onReturn: (requisitionId: number, equipmentIds: number[]) => Promise<void>;
  onDownloadGuide: (requisitionId: number) => Promise<void>;
  onSendGuide: (requisitionId: number) => Promise<void>;
  onRequestRefresh?: () => void;
}

const NEW_FORM_ID = "form-new";

export default function WarehouseRequisitionsTable({
  requisitionsData,
  equipmentOptions,
  isLoadingEquipment = false,
  canEdit,
  onCreate,
  onReturn,
  onDownloadGuide,
  onSendGuide,
  onRequestRefresh,
}: Readonly<WarehouseRequisitionsTableProps>) {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [openRowId, setOpenRowId] = useState<number | null | string>(null);
  const [isCreating, setIsCreating] = useState(false);

  const { search, setSearch, filteredData } = useRequisitionSearch(requisitionsData, () => setPage(0));

  const columnCount = canEdit ? 6 : 5;
  const isNewOpen = openRowId === "new";
  const visibleRows = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleCreate = async (values: RequisitionFormType) => {
    try {
      setIsCreating(true);
      await onCreate(values);
      setOpenRowId(null);
    } catch {
      // Error already handled by parent, keep the panel open
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Toolbar>
        <Typography sx={{ flex: "1 1 100%" }} variant="h5">
          {t("warehouse.requisitions.title")}
        </Typography>

        <Tooltip title={t("generic.filter")}>
          <IconButton onClick={() => setShowFilters((prev) => !prev)}>
            <Badge variant="dot" color="primary" invisible={!search}>
              <FilterListIcon color={showFilters ? "primary" : undefined} />
            </Badge>
          </IconButton>
        </Tooltip>

        <Tooltip title={t("generic.refresh")}>
          <IconButton onClick={() => onRequestRefresh?.()}>
            <CachedIcon />
          </IconButton>
        </Tooltip>

        {canEdit && (
          <Tooltip title={t("warehouse.requisitions.add-new")}>
            <IconButton onClick={() => setOpenRowId(isNewOpen ? null : "new")}>
              <PlusOne />
            </IconButton>
          </Tooltip>
        )}
      </Toolbar>

      {showFilters && (
        <Grid2 container spacing={3} sx={{ p: 2 }}>
          <Grid2 size={12}>
            <TextField
              name="search"
              label={t("generic.search")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth
            />
          </Grid2>
        </Grid2>
      )}

      <TableContainer>
        <Table aria-label="requisitions table">
          {!isNewOpen && (
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: "5%" }} />
                <TableCell>{t("warehouse.requisitions.equipment")}</TableCell>
                <TableCell>{t("warehouse.requisitions.requested-by")}</TableCell>
                <TableCell>{t("warehouse.requisitions.period")}</TableCell>
                <TableCell>{t("warehouse.requisitions.status")}</TableCell>
                {canEdit && (
                  <TableCell sx={{ width: "10%" }} align="right">
                    {t("generic.actions")}
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
          )}

          <TableBody>
            {isNewOpen && (
              <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={columnCount}>
                  <Collapse in timeout="auto" unmountOnExit>
                    <NewRequisitionPanel
                      formId={NEW_FORM_ID}
                      isCreating={isCreating}
                      equipmentOptions={equipmentOptions}
                      isLoadingEquipment={isLoadingEquipment}
                      onCancel={() => setOpenRowId(null)}
                      onSubmit={handleCreate}
                    />
                  </Collapse>
                </TableCell>
              </TableRow>
            )}

            {visibleRows.length === 0 && !isNewOpen && (
              <TableRow>
                <TableCell colSpan={columnCount} align="center" sx={{ py: 10 }}>
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", opacity: 0.6 }}>
                    <SearchOff sx={{ fontSize: 64, mb: 2 }} />
                    <Typography variant="h6">{t("generic.no-records")}</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}

            {visibleRows.map((row) => (
              <RequisitionRow
                key={row.id}
                row={row}
                open={openRowId === row.id}
                canEdit={canEdit}
                columnCount={columnCount}
                onToggle={() => setOpenRowId(openRowId === row.id ? null : row.id)}
                onReturn={onReturn}
                onDownloadGuide={onDownloadGuide}
                onSendGuide={onSendGuide}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={filteredData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(Number.parseInt(e.target.value, 10));
          setPage(0);
        }}
      />
    </Paper>
  );
}